// src/pages/users/passengers/Passengers.js
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Container, Row, Col, Table, Button, Modal, Form, Spinner, Alert,
  InputGroup, FormControl, Pagination, Dropdown, Tabs, Tab,
} from "react-bootstrap";
import api from "../../services/api";
import { Colors } from "../../constants/Colors";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

/* ================================================================== */
/* CONSTANTS                                                          */
/* ================================================================== */

const MySwal = withReactContent(Swal);
const ITEMS_PER_PAGE = 10;

/* ---------------- Design tokens ---------------- */
const T = {
  radius: 14,
  radiusSm: 10,
  shadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06)",
  shadowHover:
    "0 4px 8px rgba(16,24,40,.06), 0 12px 28px rgba(16,24,40,.10)",
  border: `1px solid ${Colors.BORDER_GRAY}`,
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
};

/* ---------------- Status metadata ---------------- */
const STATUS_META = {
  Active: {
    color: Colors.SUCCESS,
    bg: `${Colors.SUCCESS}1f`,
    label: "Active",
  },
  Inactive: { color: Colors.RED, bg: `${Colors.RED}1f`, label: "Inactive" },
};

const STATUS_OPTIONS = [
  { value: "All", label: "All Status" },
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

/* ---------------- Empty form ---------------- */
const EMPTY_FORM = {
  id: null,
  username: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  status: "Active",
  address: "",
  location: "",
  firebase_uid: null,
  password: "",
  latitude: null,
  longitude: null,
  last_location_update: null,
};

/* ================================================================== */
/* HELPERS                                                            */
/* ================================================================== */

const initials = (first = "", last = "") =>
  `${(first[0] || "").toUpperCase()}${(last[0] || "").toUpperCase()}` || "?";

const getStatusMeta = (status) => {
  const key =
    (status || "").charAt(0).toUpperCase() +
    (status || "").slice(1).toLowerCase();
  return (
    STATUS_META[key] || {
      color: Colors.GRAY,
      bg: `${Colors.GRAY}26`,
      label: status || "—",
    }
  );
};

const normalizeStatus = (s) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "Active";

/* Normalize API list response */
const parseListResponse = (data) => ({
  rows: data.data || [],
  total: data.total || 0,
  lastPage: data.last_page || 1,
});

/* Normalize single passenger response */
const parsePassengerResponse = (data) => data.data || data;

/* ================================================================== */
/* ALERTS                                                             */
/* ================================================================== */

const showLoadingAlert = (title = "Loading...", text = "Please wait") =>
  MySwal.fire({
    title,
    text,
    allowOutsideClick: false,
    didOpen: () => MySwal.showLoading(),
  });

const showSuccessAlert = (title, text) =>
  MySwal.fire({
    title,
    text,
    icon: "success",
    confirmButtonColor: Colors.PRIMARY,
    timer: 2000,
    timerProgressBar: true,
  });

const showErrorAlert = (title, text) =>
  MySwal.fire({
    title,
    text,
    icon: "error",
    confirmButtonColor: Colors.PRIMARY,
  });

/* ================================================================== */
/* STYLES                                                             */
/* ================================================================== */

const Styles = () => (
  <style>{`
    /* ---- Page shell ---- */
    .bk-page { font-family: ${T.font}; background: ${Colors.LIGHT_GRAY}; min-height: 100vh; padding: 24px 0 60px; }
    .bk-page .container-fluid { max-width: 1400px; }

    /* ---- Header ---- */
    .bk-header { background: linear-gradient(135deg, ${Colors.GRADIENT_START} 0%, ${Colors.GRADIENT_END} 100%); color: #fff; border-radius: ${T.radius}px; padding: 26px 30px; margin-bottom: 20px; position: relative; overflow: hidden; box-shadow: 0 10px 30px rgba(14,107,248,.22); }
    .bk-header::after { content: ""; position: absolute; top: -60px; right: -60px; width: 220px; height: 220px; background: radial-gradient(circle, rgba(255,255,255,.18) 0%, transparent 70%); border-radius: 50%; }
    .bk-header h2 { font-weight: 700; letter-spacing: -.02em; margin: 0; font-size: 22px; }
    .bk-header p { opacity: .88; margin: 4px 0 0; font-size: 13.5px; }

    /* ---- Stat cards ---- */
    .bk-stat { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 16px 18px; display: flex; align-items: center; gap: 12px; transition: transform .18s ease, box-shadow .18s ease; height: 100%; }
    .bk-stat:hover { transform: translateY(-2px); box-shadow: ${T.shadowHover}; }
    .bk-stat-icon { width: 44px; height: 44px; border-radius: 11px; display: grid; place-items: center; font-size: 19px; flex-shrink: 0; }
    .bk-stat-val { font-size: 21px; font-weight: 700; line-height: 1; color: ${Colors.TEXT_DARK}; }
    .bk-stat-lbl { font-size: 11px; color: ${Colors.TEXT_LIGHT}; text-transform: uppercase; letter-spacing: .05em; margin-top: 4px; font-weight: 600; }

    /* ---- Card ---- */
    .bk-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; box-shadow: ${T.shadow}; overflow: hidden; }
    .bk-card-header { padding: 16px 22px; border-bottom: ${T.border}; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; background: #fff; }
    .bk-card-header h5 { margin: 0; font-weight: 600; color: ${Colors.TEXT_DARK}; font-size: 15.5px; display: flex; align-items: center; gap: 8px; }
    .bk-card-header small { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; }

    /* ---- Toolbar ---- */
    .bk-toolbar { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 12px; box-shadow: ${T.shadow}; margin-bottom: 18px; }
    .bk-search { border: 1px solid transparent !important; background: ${Colors.LIGHT_GRAY} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; transition: all .18s; height: auto !important; }
    .bk-search:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: 0 0 0 3px ${Colors.PRIMARY}22 !important; }
    .bk-search-icon { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; border-right: none !important; border-radius: ${T.radiusSm} 0 0 ${T.radiusSm} !important; color: ${Colors.GRAY}; }
    .bk-filter-btn { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; font-weight: 500; text-align: left; display: flex; align-items: center; justify-content: space-between; }
    .bk-filter-btn:hover, .bk-filter-btn:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: none !important; color: ${Colors.TEXT_DARK} !important; }
    .bk-filter-btn::after { margin-left: auto; }

    /* ---- Table ---- */
    .bk-table { margin: 0; }
    .bk-table thead th { background: ${Colors.LIGHT_GRAY}; border: none; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; font-weight: 700; padding: 13px 18px; white-space: nowrap; }
    .bk-table tbody td { padding: 15px 18px; border-color: ${Colors.BORDER_GRAY}; vertical-align: middle; font-size: 13.5px; color: ${Colors.TEXT_DARK}; }
    .bk-table tbody tr { transition: background .15s; }
    .bk-table tbody tr:hover { background: ${Colors.LIGHT_GRAY}55; }

    /* ---- Avatars & chips ---- */
    .bk-avatar { width: 34px; height: 34px; border-radius: 10px; background: linear-gradient(135deg, ${Colors.GRADIENT_START}, ${Colors.GRADIENT_END}); color: #fff; font-weight: 600; font-size: 13px; display: grid; place-items: center; flex-shrink: 0; }
    .bk-id-chip { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 11px; font-weight: 600; background: ${Colors.LIGHT_GRAY}; color: ${Colors.TEXT_LIGHT}; padding: 3px 7px; border-radius: 5px; }

    /* ---- Pills ---- */
    .bk-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 600; line-height: 1.4; }
    .bk-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

    /* ---- Empty state ---- */
    .bk-empty { text-align: center; padding: 60px 20px; }
    .bk-empty-icon { width: 80px; height: 80px; margin: 0 auto 18px; border-radius: 50%; background: ${Colors.LIGHT_GRAY}; display: grid; place-items: center; font-size: 34px; color: ${Colors.GRAY}; }
    .bk-empty h5 { font-weight: 600; margin-bottom: 6px; }

    /* ---- Skeleton ---- */
    .bk-skel { background: linear-gradient(90deg, ${Colors.LIGHT_GRAY} 25%, #eee 50%, ${Colors.LIGHT_GRAY} 75%); background-size: 200% 100%; animation: bk-shimmer 1.4s infinite; border-radius: 6px; }
    @keyframes bk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

    /* ---- Buttons ---- */
    .bk-btn-primary { background: ${Colors.PRIMARY} !important; border: none !important; color: #fff !important; border-radius: ${T.radiusSm} !important; font-weight: 600; padding: 8px 16px; font-size: 13.5px; transition: all .18s; }
    .bk-btn-primary:hover { background: #e87a2e !important; transform: translateY(-1px); box-shadow: 0 6px 16px ${Colors.PRIMARY}44; }
    .bk-btn-primary:disabled { opacity: .6 !important; cursor: not-allowed !important; transform: none !important; box-shadow: none !important; }
    .bk-btn-ghost { background: transparent !important; border: 1px solid ${Colors.BORDER_GRAY} !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; font-weight: 500; padding: 7px 13px; font-size: 13px; transition: all .18s; }
    .bk-btn-ghost:hover { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; background: ${Colors.PRIMARY}0d !important; }

    /* ---- Info sections ---- */
    .bk-section { background: ${Colors.LIGHT_GRAY}; border-radius: ${T.radiusSm}; padding: 16px; margin-bottom: 14px; }
    .bk-section-title { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; margin-bottom: 12px; display: flex; align-items: center; gap: 7px; }
    .bk-section-title i { color: ${Colors.PRIMARY}; font-size: 14px; }
    .bk-info-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px dashed ${Colors.BORDER_GRAY}; font-size: 13px; gap: 12px; }
    .bk-info-row:last-child { border-bottom: none; }
    .bk-info-label { color: ${Colors.TEXT_LIGHT}; flex-shrink: 0; }
    .bk-info-value { color: ${Colors.TEXT_DARK}; font-weight: 600; text-align: right; max-width: 65%; word-break: break-word; }

    /* ---- Modal ---- */
    .modal-content { border-radius: ${T.radius}px; border: none; box-shadow: 0 24px 60px rgba(16,24,40,.18); }
    .modal-header { border-bottom: ${T.border}; padding: 18px 22px; }
    .modal-title { font-weight: 700; color: ${Colors.TEXT_DARK}; font-size: 16px; }
    .modal-body { padding: 20px 22px; }
    .modal-footer { border-top: ${T.border}; padding: 14px 22px; }

    /* ---- Pagination ---- */
    .page-item.active .page-link { background: ${Colors.PRIMARY} !important; border-color: ${Colors.PRIMARY} !important; color: #fff !important; }
    .page-link { color: ${Colors.TEXT_DARK} !important; border-color: ${Colors.BORDER_GRAY} !important; }
    .page-link:hover { background: ${Colors.LIGHT_GRAY} !important; }

    /* ---- Dropdown ---- */
    .dropdown-menu { border-radius: ${T.radiusSm}; border: ${T.border}; box-shadow: ${T.shadow}; padding: 6px; }
    .dropdown-item { border-radius: 6px; padding: 8px 12px; font-size: 13px; }
    .dropdown-item.active { background: ${Colors.PRIMARY}1a !important; color: ${Colors.TEXT_DARK} !important; }

    /* ---- Tabs ---- */
    .nav-tabs { border-bottom: ${T.border}; gap: 4px; }
    .nav-tabs .nav-link { border: none !important; color: ${Colors.TEXT_LIGHT}; font-weight: 500; font-size: 13.5px; padding: 9px 15px; border-radius: ${T.radiusSm} ${T.radiusSm} 0 0; }
    .nav-tabs .nav-link:hover { color: ${Colors.TEXT_DARK}; background: ${Colors.LIGHT_GRAY}; }
    .nav-tabs .nav-link.active { color: ${Colors.PRIMARY} !important; background: #fff !important; border-bottom: 2px solid ${Colors.PRIMARY} !important; }
  `}</style>
);

/* ================================================================== */
/* PRESENTATIONAL COMPONENTS                                          */
/* ================================================================== */

const StatusPill = ({ status }) => {
  const meta = getStatusMeta(status);
  return (
    <span className="bk-pill" style={{ color: meta.color, background: meta.bg }}>
      {meta.label}
    </span>
  );
};

/* Reusable empty/error state used in map preview */
const MapPlaceholder = ({ icon, message }) => (
  <div className="text-center py-5" style={{ color: Colors.GRAY }}>
    <div
      style={{
        width: 72,
        height: 72,
        margin: "0 auto 16px",
        borderRadius: "50%",
        background: Colors.LIGHT_GRAY,
        display: "grid",
        placeItems: "center",
        fontSize: 30,
      }}
    >
      <i className={`bi ${icon}`} />
    </div>
    <p className="mb-0" style={{ color: Colors.TEXT_LIGHT }}>
      {message}
    </p>
  </div>
);

/* ================================================================== */
/* MAIN COMPONENT                                                     */
/* ================================================================== */

const Passengers = () => {
  const navigate = useNavigate();
  const abortControllerRef = useRef(null);

  /* ---------------- List state ---------------- */
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* ---------------- Modal state ---------------- */
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState("view"); // "view" | "add"
  const [updating, setUpdating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  /* ---------------- Map state ---------------- */
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");
  const [mapData, setMapData] = useState(null);

  /* ================================================================ */
  /* DATA FETCHING                                                    */
  /* ================================================================ */

  const fetchPassengers = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/admin/passengers", {
          params: {
            page: currentPage,
            per_page: ITEMS_PER_PAGE,
            search: searchQuery || undefined,
            status: statusFilter !== "All" ? statusFilter : undefined,
          },
          signal,
        });
        const { rows, total, lastPage } = parseListResponse(data);
        setPassengers(rows);
        setTotalPages(lastPage);
        setTotalItems(total);
      } catch (err) {
        if (err.name === "AbortError" || err.code === "ERR_CANCELED") return;
        console.error("Error fetching passengers:", err);
        if (err.response?.status === 401) {
          localStorage.removeItem("arkila_admin_token");
          navigate("/login");
          showErrorAlert("Session Expired", "Please log in again.");
        } else if (err.response?.status === 404) {
          showErrorAlert("Not Found", "Passengers API endpoint not found.");
        } else {
          showErrorAlert(
            "Error",
            err.response?.data?.message || "Failed to load passengers."
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [currentPage, searchQuery, statusFilter, navigate]
  );

  useEffect(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    fetchPassengers(abortControllerRef.current.signal);
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchPassengers]);

  /* ================================================================ */
  /* LOCATION                                                         */
  /* ================================================================ */

  const fetchPassengerLocation = async (passengerId) => {
    setMapLoading(true);
    setMapError("");
    setMapData(null);
    try {
      const { data } = await api.get(
        `/admin/passengers/${passengerId}/location`
      );
      if (data.success && data.data) {
        const p = data.data;
        if (p.latitude && p.longitude) {
          setMapData({
            latitude: p.latitude,
            longitude: p.longitude,
            last_update: p.last_location_update || null,
            location: p.location || null,
            address: p.address || null,
          });
        } else {
          setMapError("No location data available for this passenger");
        }
      } else {
        setMapError("Failed to fetch passenger location details");
      }
    } catch (err) {
      setMapError(
        "Failed to fetch location: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setMapLoading(false);
    }
  };

  /* ================================================================ */
  /* MODAL ACTIONS                                                    */
  /* ================================================================ */

  const openModal = async (id = null) => {
    try {
      if (id) {
        const { data } = await api.get(`/admin/passengers/${id}`);
        const p = parsePassengerResponse(data);
        setForm({
          id: p.id || null,
          username: p.username || "",
          first_name: p.first_name || "",
          last_name: p.last_name || "",
          email: p.email || "",
          phone: p.phone || "",
          status: normalizeStatus(p.status),
          address: p.address || "",
          location: p.location || "",
          firebase_uid: p.firebase_uid || null,
          password: "",
          latitude: p.latitude || null,
          longitude: p.longitude || null,
          last_location_update: p.updated_at || null,
        });
        fetchPassengerLocation(id);
        setMode("view");
      } else {
        setForm(EMPTY_FORM);
        setMode("add");
      }
      setErrors({});
      setMapData(null);
      setMapError("");
      setShowModal(true);
    } catch (err) {
      console.error("Error opening modal:", err);
      if (err.response?.status === 404)
        showErrorAlert("Not Found", "Passenger not found.");
      else if (err.response?.status === 401) {
        localStorage.removeItem("arkila_admin_token");
        navigate("/login");
      } else showErrorAlert("Error", "Failed to load passenger details.");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setErrors({});

    /* Client-side validation */
    const newErrors = {};
    if (!form.first_name?.trim())
      newErrors.first_name = "First name is required";
    if (!form.last_name?.trim())
      newErrors.last_name = "Last name is required";
    if (!form.email?.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email.trim()))
      newErrors.email = "Please enter a valid email address";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setUpdating(false);
      showErrorAlert(
        "Validation Error",
        "Please fill in all required fields correctly."
      );
      return;
    }

    try {
      showLoadingAlert(
        "Creating...",
        "Please wait while we create the passenger"
      );

      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone?.trim() || null,
        status: "active",
        address: form.address?.trim() || null,
        location: form.location?.trim() || null,
        username: form.username?.trim() || null,
      };
      if (form.password && form.password.trim().length >= 6)
        payload.password = form.password.trim();

      await api.post("/admin/passengers", payload);

      MySwal.close();
      setSuccess("Passenger created successfully!");
      showSuccessAlert("Success!", "Passenger created successfully!");

      setShowModal(false);
      const newController = new AbortController();
      abortControllerRef.current = newController;
      fetchPassengers(newController.signal);

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error saving passenger:", err);
      MySwal.close();
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setErrors(validationErrors);
        const errorMessages = Object.entries(validationErrors).map(
          ([field, messages]) =>
            `${field
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) =>
                l.toUpperCase()
              )}: ${
              Array.isArray(messages) ? messages.join(", ") : messages
            }`
        );
        showErrorAlert(
          "Validation Error",
          errorMessages.length > 0
            ? errorMessages.join("\n")
            : "Please correct the form errors."
        );
      } else if (err.response?.status === 401) {
        localStorage.removeItem("arkila_admin_token");
        navigate("/login");
      } else {
        showErrorAlert(
          "Error",
          err.response?.data?.message || "Failed to create passenger."
        );
      }
    } finally {
      setUpdating(false);
    }
  };

  /* ================================================================ */
  /* MAP PREVIEW                                                      */
  /* ================================================================ */

  const renderMapPreview = () => {
    if (mapLoading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted mb-0">Loading location data…</p>
        </div>
      );
    }

    if (mapError) {
      return <MapPlaceholder icon="bi-geo-alt" message={mapError} />;
    }

    if (mapData) {
      const mapUrl = `https://maps.google.com/maps?q=${mapData.latitude},${mapData.longitude}&z=15&output=embed`;
      return (
        <div>
          <div className="bk-section">
            <div className="bk-section-title">
              <i className="bi bi-clock-history" /> Last Known Location
            </div>
            <div className="bk-info-row">
              <span className="bk-info-label">Updated</span>
              <span className="bk-info-value">
                {mapData.last_update
                  ? new Date(mapData.last_update).toLocaleString()
                  : "N/A"}
              </span>
            </div>
            {(mapData.address || mapData.location) && (
              <div className="bk-info-row">
                <span className="bk-info-label">Address</span>
                <span className="bk-info-value">
                  {mapData.address || mapData.location}
                </span>
              </div>
            )}
            <div className="bk-info-row">
              <span className="bk-info-label">Coordinates</span>
              <span
                className="bk-info-value"
                style={{
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 12,
                }}
              >
                {mapData.latitude}, {mapData.longitude}
              </span>
            </div>
          </div>

          <div
            style={{
              borderRadius: T.radiusSm,
              overflow: "hidden",
              border: T.border,
              aspectRatio: "16 / 9",
            }}
          >
            <iframe
              src={mapUrl}
              style={{
                border: 0,
                width: "100%",
                height: "100%",
                display: "block",
              }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Passenger Location"
            />
          </div>
        </div>
      );
    }

    return <MapPlaceholder icon="bi-geo-alt" message="No location data" />;
  };

  /* ================================================================ */
  /* PAGINATION                                                       */
  /* ================================================================ */

  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const paginate = (p) => setCurrentPage(p);

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      currentPage - Math.floor(maxVisiblePages / 2)
    );
    const endPage = Math.min(
      totalPages,
      startPage + maxVisiblePages - 1
    );
    if (endPage - startPage + 1 < maxVisiblePages)
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    for (let n = startPage; n <= endPage; n++) {
      items.push(
        <Pagination.Item
          key={n}
          active={n === currentPage}
          onClick={() => paginate(n)}
        >
          {n}
        </Pagination.Item>
      );
    }
    return items;
  };

  /* ================================================================ */
  /* LOADING SKELETON                                                 */
  /* ================================================================ */

  if (loading && passengers.length === 0) {
    return (
      <div className="bk-page">
        <Styles />
        <Container fluid>
          <div className="bk-header">
            <h2>Passengers Management</h2>
            <p>Loading passenger accounts…</p>
          </div>
          <Row className="g-3 mb-4">
            {[1, 2, 3].map((i) => (
              <Col key={i} xs={12} md={4}>
                <div className="bk-stat">
                  <div
                    className="bk-skel"
                    style={{ width: 44, height: 44, borderRadius: 11 }}
                  />
                  <div className="flex-grow-1">
                    <div
                      className="bk-skel mb-2"
                      style={{ height: 18, width: "50%" }}
                    />
                    <div
                      className="bk-skel"
                      style={{ height: 11, width: "70%" }}
                    />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <div className="bk-card" style={{ padding: 20 }}>
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bk-skel mb-3"
                style={{ height: 42 }}
              />
            ))}
          </div>
        </Container>
      </div>
    );
  }

  /* ================================================================ */
  /* MAIN RENDER                                                      */
  /* ================================================================ */

  const activeCount = passengers.filter(
    (p) => (p.status || "").toLowerCase() === "active"
  ).length;
  const inactiveCount = passengers.filter(
    (p) => (p.status || "").toLowerCase() === "inactive"
  ).length;

  return (
    <div className="bk-page">
      <Styles />
      <Container fluid>
        {/* ============ HEADER ============ */}
        <div className="bk-header">
          <div className="position-relative" style={{ zIndex: 1 }}>
            <h2>Passengers Management</h2>
            <p>
              View all passenger accounts and their last known locations
            </p>
          </div>
        </div>

        {/* ============ ALERTS ============ */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError("")}>
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccess("")}
          >
            <i className="bi bi-check-circle me-2" />
            {success}
          </Alert>
        )}

        {/* ============ STATS ============ */}
        <Row className="g-3 mb-4">
          {[
            {
              k: "Active",
              v: activeCount,
              i: "bi-person-check",
              c: Colors.SUCCESS,
            },
            {
              k: "Inactive",
              v: inactiveCount,
              i: "bi-person-dash",
              c: Colors.RED,
            },
            {
              k: "Total",
              v: totalItems,
              i: "bi-people",
              c: Colors.PRIMARY,
            },
          ].map((s) => (
            <Col key={s.k} xs={12} md={4}>
              <div className="bk-stat">
                <div
                  className="bk-stat-icon"
                  style={{ background: `${s.c}1f`, color: s.c }}
                >
                  <i className={`bi ${s.i}`} />
                </div>
                <div>
                  <div className="bk-stat-val">{s.v}</div>
                  <div className="bk-stat-lbl">{s.k}</div>
                </div>
              </div>
            </Col>
          ))}
        </Row>

        {/* ============ TOOLBAR ============ */}
        <div className="bk-toolbar">
          <Row className="g-2">
            <Col md={8}>
              <InputGroup>
                <InputGroup.Text className="bk-search-icon">
                  <i className="bi bi-search" />
                </InputGroup.Text>
                <FormControl
                  className="bk-search"
                  placeholder="Search by name, username, email, or phone…"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </InputGroup>
            </Col>
            <Col md={4}>
              <Dropdown>
                <Dropdown.Toggle className="bk-filter-btn w-100">
                  <i className="bi bi-funnel me-2" />
                  {
                    STATUS_OPTIONS.find((o) => o.value === statusFilter)
                      ?.label
                  }
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100">
                  {STATUS_OPTIONS.map((o) => (
                    <Dropdown.Item
                      key={o.value}
                      active={statusFilter === o.value}
                      onClick={() => {
                        setStatusFilter(o.value);
                        setCurrentPage(1);
                      }}
                    >
                      {o.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
        </div>

        {/* ============ TABLE ============ */}
        <div className="bk-card">
          <div className="bk-card-header">
            <h5>
              <i
                className="bi bi-people"
                style={{ color: Colors.PRIMARY }}
              />
              Passenger Accounts
            </h5>
            <small>
              Showing {Math.min(indexOfFirstItem + 1, totalItems)}–
              {Math.min(indexOfLastItem, totalItems)} of {totalItems}
            </small>
          </div>

          {passengers.length === 0 ? (
            <div className="bk-empty">
              <div className="bk-empty-icon">
                <i className="bi bi-people" />
              </div>
              <h5 style={{ color: Colors.TEXT_DARK }}>
                No passengers found
              </h5>
              <p className="text-muted mb-0">
                {searchQuery || statusFilter !== "All"
                  ? "Try adjusting your search or filter."
                  : "There are no passengers in the system yet."}
              </p>
            </div>
          ) : (
            <>
              <Table responsive hover className="bk-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Passenger</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Status</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {passengers.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <span className="bk-id-chip">#{p.id}</span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div className="bk-avatar">
                            {initials(p.first_name, p.last_name)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600 }}>
                              {p.first_name} {p.last_name}
                            </div>
                            {p.address && (
                              <small className="text-muted">
                                {p.address.length > 30
                                  ? p.address.slice(0, 30) + "…"
                                  : p.address}
                              </small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span style={{ color: Colors.TEXT_LIGHT }}>
                          {p.username || "—"}
                        </span>
                      </td>
                      <td>{p.email}</td>
                      <td>{p.phone || "—"}</td>
                      <td>
                        <StatusPill status={p.status} />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Button
                          className="bk-btn-ghost"
                          size="sm"
                          onClick={() => openModal(p.id)}
                          disabled={updating}
                        >
                          <i className="bi bi-eye me-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div
                  className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                  style={{ padding: "14px 20px", borderTop: T.border }}
                >
                  <small className="text-muted">
                    Page {currentPage} of {totalPages}
                  </small>
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => paginate(currentPage - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => paginate(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>

        {/* ============ MODAL ============ */}
        <Modal
          show={showModal}
          onHide={() => !updating && setShowModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton={!updating}>
            <Modal.Title>
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: `${Colors.PRIMARY}1f`,
                    color: Colors.PRIMARY,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <i
                    className={`bi ${
                      mode === "view"
                        ? "bi-person-badge"
                        : "bi-person-plus"
                    }`}
                  />
                </span>
                {mode === "view"
                  ? "Passenger Details"
                  : "Add New Passenger"}
              </div>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {mode === "view" ? (
              <Tabs defaultActiveKey="details" className="mb-3">
                <Tab eventKey="details" title="Details">
                  <div
                    style={{
                      maxHeight: "60vh",
                      overflowY: "auto",
                      paddingRight: "0.25rem",
                    }}
                  >
                    {/* Identity */}
                    <div className="bk-section">
                      <div className="bk-section-title">
                        <i className="bi bi-person" /> Identity
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="bk-avatar"
                          style={{
                            width: 48,
                            height: 48,
                            fontSize: 16,
                            borderRadius: 14,
                          }}
                        >
                          {initials(form.first_name, form.last_name)}
                        </div>
                        <div>
                          <div
                            style={{
                              fontWeight: 700,
                              color: Colors.TEXT_DARK,
                            }}
                          >
                            {form.first_name || "—"} {form.last_name || ""}
                          </div>
                          <small className="text-muted d-block">
                            {form.username
                              ? `@${form.username}`
                              : "No username"}
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* Contact */}
                    <div className="bk-section">
                      <div className="bk-section-title">
                        <i className="bi bi-envelope" /> Contact
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">Email</span>
                        <span className="bk-info-value">
                          {form.email || "—"}
                        </span>
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">Phone</span>
                        <span className="bk-info-value">
                          {form.phone || "—"}
                        </span>
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">Address</span>
                        <span className="bk-info-value">
                          {form.address || "—"}
                        </span>
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">Location</span>
                        <span className="bk-info-value">
                          {form.location || "—"}
                        </span>
                      </div>
                    </div>

                    {/* Account */}
                    <div className="bk-section" style={{ marginBottom: 0 }}>
                      <div className="bk-section-title">
                        <i className="bi bi-shield-check" /> Account
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">Status</span>
                        <StatusPill status={form.status} />
                      </div>
                      <div className="bk-info-row">
                        <span className="bk-info-label">Firebase UID</span>
                        <span
                          className="bk-info-value"
                          style={{
                            fontFamily: "ui-monospace, monospace",
                            fontSize: 11,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {form.firebase_uid || "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                </Tab>
                <Tab eventKey="location" title="Location">
                  {renderMapPreview()}
                </Tab>
              </Tabs>
            ) : (
              <Form id="passengerForm" onSubmit={handleSave}>
                <div
                  style={{
                    maxHeight: "60vh",
                    overflowY: "auto",
                    paddingRight: "0.25rem",
                  }}
                >
                  {/* Identity */}
                  <div className="bk-section">
                    <div className="bk-section-title">
                      <i className="bi bi-person" /> Identity
                    </div>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label
                            style={{ fontSize: 13, fontWeight: 500 }}
                          >
                            First Name *
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={form.first_name}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                first_name: e.target.value,
                              })
                            }
                            isInvalid={!!errors.first_name}
                            required
                            placeholder="Enter first name"
                            style={{ borderRadius: T.radiusSm }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {Array.isArray(errors.first_name)
                              ? errors.first_name[0]
                              : errors.first_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label
                            style={{ fontSize: 13, fontWeight: 500 }}
                          >
                            Last Name *
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={form.last_name}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                last_name: e.target.value,
                              })
                            }
                            isInvalid={!!errors.last_name}
                            required
                            placeholder="Enter last name"
                            style={{ borderRadius: T.radiusSm }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {Array.isArray(errors.last_name)
                              ? errors.last_name[0]
                              : errors.last_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label
                            style={{ fontSize: 13, fontWeight: 500 }}
                          >
                            Username
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={form.username}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                username: e.target.value,
                              })
                            }
                            isInvalid={!!errors.username}
                            placeholder="Optional unique username"
                            style={{ borderRadius: T.radiusSm }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {Array.isArray(errors.username)
                              ? errors.username[0]
                              : errors.username}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* Contact */}
                  <div className="bk-section">
                    <div className="bk-section-title">
                      <i className="bi bi-envelope" /> Contact
                    </div>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label
                            style={{ fontSize: 13, fontWeight: 500 }}
                          >
                            Email *
                          </Form.Label>
                          <Form.Control
                            type="email"
                            value={form.email}
                            onChange={(e) =>
                              setForm({ ...form, email: e.target.value })
                            }
                            isInvalid={!!errors.email}
                            required
                            placeholder="name@example.com"
                            style={{ borderRadius: T.radiusSm }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {Array.isArray(errors.email)
                              ? errors.email[0]
                              : errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label
                            style={{ fontSize: 13, fontWeight: 500 }}
                          >
                            Phone
                          </Form.Label>
                          <Form.Control
                            type="tel"
                            value={form.phone || ""}
                            onChange={(e) =>
                              setForm({ ...form, phone: e.target.value })
                            }
                            isInvalid={!!errors.phone}
                            placeholder="Optional"
                            style={{ borderRadius: T.radiusSm }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {Array.isArray(errors.phone)
                              ? errors.phone[0]
                              : errors.phone}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label
                            style={{ fontSize: 13, fontWeight: 500 }}
                          >
                            Address
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={form.address || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                address: e.target.value,
                              })
                            }
                            isInvalid={!!errors.address}
                            placeholder="Optional"
                            style={{ borderRadius: T.radiusSm }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {Array.isArray(errors.address)
                              ? errors.address[0]
                              : errors.address}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label
                            style={{ fontSize: 13, fontWeight: 500 }}
                          >
                            Location
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={form.location || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                location: e.target.value,
                              })
                            }
                            isInvalid={!!errors.location}
                            placeholder="Optional city/region"
                            style={{ borderRadius: T.radiusSm }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {Array.isArray(errors.location)
                              ? errors.location[0]
                              : errors.location}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  {/* Security */}
                  <div className="bk-section" style={{ marginBottom: 0 }}>
                    <div className="bk-section-title">
                      <i className="bi bi-shield-lock" /> Security
                    </div>
                    <Form.Group>
                      <Form.Label
                        style={{ fontSize: 13, fontWeight: 500 }}
                      >
                        Password (optional)
                      </Form.Label>
                      <Form.Control
                        type="password"
                        value={form.password || ""}
                        onChange={(e) =>
                          setForm({ ...form, password: e.target.value })
                        }
                        isInvalid={!!errors.password}
                        placeholder="Minimum 6 characters"
                        minLength={6}
                        style={{ borderRadius: T.radiusSm }}
                      />
                      <Form.Control.Feedback type="invalid">
                        {Array.isArray(errors.password)
                          ? errors.password[0]
                          : errors.password}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        Passenger will be set to{" "}
                        <strong>Active</strong> automatically.
                      </Form.Text>
                    </Form.Group>
                  </div>
                </div>
              </Form>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              className="bk-btn-ghost"
              onClick={() => setShowModal(false)}
              disabled={updating}
            >
              Close
            </Button>
            {mode === "add" && (
              <Button
                type="submit"
                form="passengerForm"
                className="bk-btn-primary"
                disabled={updating}
              >
                {updating ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Creating…
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-lg me-1" />
                    Create Passenger
                  </>
                )}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default Passengers;